<script setup>
  import { ref, computed, useTemplateRef, watch, watchEffect, nextTick, onMounted, onUnmounted } from 'vue'
  import { useUserStore } from "@/stores/UserStore.js";
  import { useGoogleAnalyticsStore } from '@/stores/GoogleAnalyticsStore';

  import { useCookie } from "@/composables/useCookie.js";
  import useConfigurationGlobals from '@/composables/useConfigurationGlobals';
  import BaseModal from "@/components/base/BaseModal.vue";
  import Icon from "@/components/icons/Icon.vue";

  const props = defineProps({
    autoOpen: {
      type: Boolean,
      default: true
    }
  })

  const userStore = useUserStore();
  const googleAnalyticsStore = useGoogleAnalyticsStore();
  const cookie = useCookie();
  const { $BRAND } = useConfigurationGlobals();

  const messagesContainer = useTemplateRef('messages-container');

  const cookieName = `msg-${userStore.messages[0].Id}-dismissed`;
  const messageExpiration = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
  const messageType = parseInt(userStore.messages[0].Type);

  const hideMessage = ref(cookie.get(cookieName) === 'true');
  const showModal = ref(!hideMessage.value && props.autoOpen);

  const message = computed(() => `${userStore.messages[0].Message}`);
  const iconName = computed(() => messageType === 401 ? 'premium-badge' : messageType === 402 ? 'stopwatch' : false);
  const messageClasses = computed(() => ['jwx-promo-modal', {'jwx-promo-modal--icon': iconName.value}]);

  cookie.set(cookieName, hideMessage.value, { expires: messageExpiration });

  const attachedAnalyticsToMessageLinks = (links) => {
    if (!links) return;

    links.forEach(link => {
      const url = new URL(link.href);

      link.addEventListener('click', (event) => {
        event.preventDefault();

        googleAnalyticsStore.sendEvent("Message_Link_Click", {
            "Hostname": url.hostname,
            "Pathname": url.pathname,
            "Search": url.search,
          });

          window.location = link.href;
      });
    });
  }

  const wireUpMessageContent = async () => {
    await nextTick();
    if (!messagesContainer.value) return;

    const messageLinks = messagesContainer.value.querySelectorAll('.jwx-promo-modal__text a');

    attachedAnalyticsToMessageLinks(messageLinks);
  }

  const handleShowPromoModal = () => {
    showModal.value = true;
    hideMessage.value = false;
  }

  const handleDismiss = () => {
    hideMessage.value = true;
    showModal.value = false;
  }

  onMounted(() => {
    window.$EventBus.addEventListener('jwx-show-promo-modal', handleShowPromoModal);
  });

  onUnmounted(() => {
    window.$EventBus.removeEventListener('jwx-show-promo-modal', handleShowPromoModal);
  });

  watchEffect(() => cookie.set(cookieName, hideMessage.value, { expires: messageExpiration }));

  watch(showModal, (visible) => {
    if (visible) wireUpMessageContent();
  });

</script>

<template>
  <BaseModal
    v-model="showModal"
    :unstyled="true"
    styleClasses="jwx-promo-modal-container"
    hide-close-button
  >  
    <div class="jwx-map__messages">
      <div
        :class="messageClasses"
        :data-message-type="userStore.messages[0].Type"
        :data-brand="$BRAND.toLowerCase()"
      >
        <div class="jwx-promo-modal__header">
          <div class="jwx-promo-modal__header-background">
            <img src="../assets/images/promo/wave-image@2x.png" class="jwx-promo-modal__header-background-image" />
          </div>
          <img src="../assets/images/promo/ikite-website-promo-devices@2x.png" class="jwx-promo-modal__hero" />
        </div>
        <div class="jwx-promo-modal__body">
          <Icon v-if="iconName" :name="iconName" class="jwx-promo-modal__icon" />
          <p class="jwx-promo-modal__content">
            <span ref="messages-container" v-html="message" class="jwx-promo-modal__text"></span>
          </p>
          <div class="jwx-promo-modal__dismiss" @click.prevent="handleDismiss">Not Now</div>
        </div>
      </div>
    </div>
  </BaseModal>
</template>